import { Controller } from "@hotwired/stimulus"

function writeTextToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text)
  }

  return new Promise((resolve, reject) => {
    const textarea = document.createElement("textarea")
    textarea.value = text
    textarea.style.position = "fixed"
    textarea.style.opacity = "0"
    document.body.appendChild(textarea)
    textarea.select()

    try {
      document.execCommand("copy") ? resolve() : reject(new Error("Copy command failed"))
    } catch (error) {
      reject(error)
    } finally {
      document.body.removeChild(textarea)
    }
  })
}

// キャプション生成ページ: SNSの複数選択・禁止事項の編集・ファイルプレビュー・結果コピー
export default class extends Controller {
  static targets = [
    "snsCheckbox",
    "accountGroup",
    "accountSelect",
    "submitButton",
    "submitHint",
    "fileInput",
    "previewArea",
    "ruleText",
    "ruleTargetSelect",
    "rulesData",
  ]

  connect() {
    if (this.hasRulesDataTarget) {
      this.rules = JSON.parse(this.rulesDataTarget.textContent || "{}")
    }
    this.objectUrls = []
    this.refresh()
  }

  disconnect() {
    this.revokeObjectUrls()
  }

  toggleSns(event) {
    const sns = event.target.dataset.sns
    const checked = event.target.checked
    const group = this.accountGroupTargets.find((g) => g.dataset.sns === sns)
    if (group) {
      group.hidden = !checked
      const select = group.querySelector("select")
      if (select) select.disabled = !checked
    }
    this.refresh()
  }

  accountChanged() {
    this.refresh()
  }


  // 選択中のSNS×アカウントに応じて、送信ボタンの活性・非活性理由・禁止事項の対象リストを更新する
  refresh() {
    const selected = this.selectedTargets()
    const quotaExhausted =
      this.hasSubmitButtonTarget && this.submitButtonTarget.dataset.quotaExhausted === "true"

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = selected.length === 0 || quotaExhausted
    }

    // ボタンが押せない理由を明示する(静かに何も起きない状態を作らない)
    if (this.hasSubmitHintTarget) {
      if (quotaExhausted) {
        this.submitHintTarget.textContent = "今月の生成上限に達しています"
      } else if (selected.length === 0) {
        this.submitHintTarget.textContent = "生成するSNSアカウントを選択してください"
      } else {
        this.submitHintTarget.textContent = ""
      }
    }

    if (this.hasRuleTargetSelectTarget) {
      const previous = this.ruleTargetSelectTarget.value
      this.ruleTargetSelectTarget.innerHTML = ""

      // 共通ルールは選択状態によらず常に編集できる
      const commonOption = document.createElement("option")
      commonOption.value = "common"
      commonOption.textContent = "共通(全アカウント)"
      this.ruleTargetSelectTarget.appendChild(commonOption)

      selected.forEach(({ sns, accountId, label }) => {
        const option = document.createElement("option")
        option.value = `${sns}:${accountId}`
        option.textContent = label
        this.ruleTargetSelectTarget.appendChild(option)
      })
      const values = [ "common", ...selected.map((t) => `${t.sns}:${t.accountId}`) ]
      if (values.includes(previous)) {
        this.ruleTargetSelectTarget.value = previous
      }
      this.syncRuleText()
    }
  }

  selectedTargets() {
    return this.accountSelectTargets
      .filter((select) => !select.disabled && select.value)
      .map((select) => ({
        sns: select.dataset.sns,
        accountId: select.value,
        label: `${this.snsLabelFor(select.dataset.sns)} / ${select.options[select.selectedIndex].textContent.trim()}`,
      }))
  }

  ruleTargetChanged() {
    this.syncRuleText()
  }

  syncRuleText() {
    if (!this.hasRuleTextTarget || !this.hasRuleTargetSelectTarget) return
    const value = this.ruleTargetSelectTarget.value
    if (!value) {
      this.ruleTextTarget.value = ""
      return
    }
    this.ruleTextTarget.value = this.rules[this.ruleKeyFor(value)] || ""
  }

  // ルール対象セレクトの値 → rulesData のキー
  ruleKeyFor(value) {
    if (value === "common") return "common"
    const [sns, accountId] = value.split(":")
    return `${this.accountTypeFor(sns)}:${accountId}`
  }

  accountTypeFor(sns) {
    return {
      instagram: "ClInstManageAccount",
      x: "ClXManageAccount",
      threads: "ClThreadManageUser",
      tiktok: "ClTiktokManageAccount",
    }[sns]
  }

  snsLabelFor(sns) {
    return {
      instagram: "Instagram",
      x: "X",
      threads: "Threads",
      tiktok: "TikTok",
    }[sns] || sns
  }

  async saveRule(event) {
    if (!this.hasRuleTargetSelectTarget || !this.ruleTargetSelectTarget.value) return
    const value = this.ruleTargetSelectTarget.value
    const url = event.currentTarget.dataset.captionGenerationUpsertUrl
    const body = new FormData()
    if (value === "common") {
      body.append("sns_type", "common")
    } else {
      const [sns, accountId] = value.split(":")
      body.append("sns_type", sns)
      body.append("sns_account_id", accountId)
    }
    body.append("rule_text", this.ruleTextTarget.value)

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content },
        body,
      })
      if (response.ok) {
        this.rules[this.ruleKeyFor(value)] = this.ruleTextTarget.value
        alert("禁止事項を保存しました")
      } else {
        const payload = await response.json().catch(() => ({}))
        const detail = payload.errors ? `\n${payload.errors.join("\n")}` : ""
        alert(`禁止事項の保存に失敗しました${detail}`)
      }
    } catch (error) {
      alert("通信に失敗しました。時間をおいて再度お試しください。")
    }
  }

  previewFiles() {
    this.revokeObjectUrls()
    this.previewAreaTarget.innerHTML = ""
    Array.from(this.fileInputTarget.files).forEach((file) => {
      const wrapper = document.createElement("div")
      wrapper.className = "flex flex-col items-center gap-1"

      if (file.type.startsWith("image/")) {
        const url = URL.createObjectURL(file)
        this.objectUrls.push(url)
        const img = document.createElement("img")
        img.src = url
        img.alt = file.name
        img.title = file.name
        img.className = "w-40 h-40 object-contain rounded-lg border border-base-300 bg-base-200"
        wrapper.appendChild(img)
      } else {
        const box = document.createElement("div")
        box.className = "w-40 h-40 rounded-lg border border-base-300 bg-base-200 flex items-center justify-center"
        box.title = file.name
        box.innerHTML = '<span class="iconify lucide--video size-6 text-base-content/50"></span>'
        wrapper.appendChild(box)
      }

      const label = document.createElement("span")
      label.className = "text-[10px] text-base-content/50 max-w-40 truncate"
      label.textContent = file.name
      wrapper.appendChild(label)
      this.previewAreaTarget.appendChild(wrapper)
    })
  }

  revokeObjectUrls() {
    ;(this.objectUrls || []).forEach((url) => URL.revokeObjectURL(url))
    this.objectUrls = []
  }

  copy(event) {
    const button = event.currentTarget
    const original = button.innerHTML

    return writeTextToClipboard(event.params.text)
      .then(() => {
        button.textContent = "コピーしました"
        setTimeout(() => {
          button.innerHTML = original
        }, 1500)
      })
      .catch(() => {
        alert("コピーに失敗しました。手動で選択してコピーしてください。")
      })
  }
}
